import {
  ANNOTATION_EDIT_URL,
  ANNOTATION_VIEW_URL,
  Entity,
  RELATION_OWNED_BY,
  RELATION_PART_OF,
} from '@backstage/catalog-model';
import { CodeSnippet, Table, TableColumn, TableProps, WarningPanel } from '@backstage/core-components';
import {
  getEntityRelations,
  humanizeEntityRef,
  useEntityList,
  useStarredEntities,
} from '@backstage/plugin-catalog-react';
import { Typography } from '@material-ui/core';
import { withStyles } from '@material-ui/core/styles';
import Edit from '@material-ui/icons/Edit';
import OpenInNew from '@material-ui/icons/OpenInNew';
import Star from '@material-ui/icons/Star';
import StarBorder from '@material-ui/icons/StarBorder';
import React, { useContext, useMemo } from 'react';
import { columnFactories } from './columns';
import { EntityTableRow } from './types';
import { CatalogContext, CatalogContextType, UserFilterContext, UserFilterContextType } from '../../../../context';

const pluralize = require('pluralize');

const YellowStar = withStyles({
  root: {
    color: '#f3ba37',
  },
})(Star);

const refCompare = (a: Entity, b: Entity) => {
  const toRef = (entity: Entity) =>
    entity.metadata.title ||
    humanizeEntityRef(entity, {
      defaultKind: 'Component',
    });

  return toRef(a).localeCompare(toRef(b));
};

/** @public */
export const EntityTable = () => {
  const { columns, actions, tableOptions, emptyContent } = useContext(CatalogContext) as CatalogContextType;
  const { isStarredEntity, toggleStarredEntity } = useStarredEntities();
  const { userFilter } = useContext(UserFilterContext) as UserFilterContextType;
  const { loading, error, entities, filters } = useEntityList();

  const defaultColumns: TableColumn<EntityTableRow>[] = useMemo(() => {
    return [
      columnFactories.createTitleColumn({ hidden: true }),
      columnFactories.createNameColumn({ defaultKind: filters.kind?.value }),
      ...columnFactories.createEntitySpecificColumns(entities, filters.kind?.value),
      columnFactories.createMetadataDescriptionColumn(),
      columnFactories.createTagsColumn(),
    ];
  }, [filters.kind?.value, entities]);

  const showTypeColumn = filters.type === undefined;
  // TODO(timbonicus): remove the title from the CatalogTable once using EntitySearchBar
  const titlePreamble = userFilter === 'My' && filters.kind?.value === 'user' ? 'My Teammates' : userFilter;

  if (error) {
    return (
      <div>
        <WarningPanel severity="error" title="Could not fetch catalog entities.">
          <CodeSnippet language="text" text={error.toString()} />
        </WarningPanel>
      </div>
    );
  }

  const defaultActions: TableProps<EntityTableRow>['actions'] = [
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_VIEW_URL];
      const title = 'View';

      return {
        icon: () => (
          <>
            <Typography variant="srOnly">{title}</Typography>
            <OpenInNew fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const url = entity.metadata.annotations?.[ANNOTATION_EDIT_URL];
      const title = 'Edit';

      return {
        icon: () => (
          <>
            <Typography variant="srOnly">{title}</Typography>
            <Edit fontSize="small" />
          </>
        ),
        tooltip: title,
        disabled: !url,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank');
        },
      };
    },
    ({ entity }) => {
      const isStarred = isStarredEntity(entity);
      const title = isStarred ? 'Remove from favorites' : 'Add to favorites';

      return {
        cellStyle: { paddingLeft: '1em' },
        icon: () => (
          <>
            <Typography variant="srOnly">{title}</Typography>
            {isStarred ? <YellowStar /> : <StarBorder />}
          </>
        ),
        tooltip: title,
        onClick: () => toggleStarredEntity(entity),
      };
    },
  ];

  const rows = entities.sort(refCompare).map(entity => {
    const partOfSystemRelations = getEntityRelations(entity, RELATION_PART_OF, {
      kind: 'system',
    });
    const ownedByRelations = getEntityRelations(entity, RELATION_OWNED_BY);

    return {
      entity,
      resolved: {
        name: humanizeEntityRef(entity, {
          defaultKind: 'Component',
        }),
        ownedByRelationsTitle: ownedByRelations.map(r => humanizeEntityRef(r, { defaultKind: 'group' })).join(', '),
        ownedByRelations,
        partOfSystemRelationTitle: partOfSystemRelations
          .map(r =>
            humanizeEntityRef(r, {
              defaultKind: 'system',
            })
          )
          .join(', '),
        partOfSystemRelations,
      },
    };
  });

  const displayKind = useMemo(() => {
    if (filters.kind?.value === 'system') {
      return 'Projects';
    } else if (filters.kind?.value === 'group') {
      return 'Team';
    } else if (filters.kind?.value === 'user' && userFilter === 'My') {
      return '';
    } else if (filters.kind?.value === 'API') {
      return 'APIs';
    }
    const str = filters.kind?.value || '';
    return str.charAt(0).toUpperCase() + str.slice(1);
  }, [filters.kind?.value, userFilter]);

  const typeColumn = (columns || defaultColumns).find(c => c.title === 'Type');
  if (typeColumn) {
    typeColumn.hidden = !showTypeColumn;
  }

  const showPagination = rows.length > 20;

  const currentType = filters.type?.value || '';
  const titleDisplay = [titlePreamble, currentType, pluralize(displayKind)].filter(s => s).join(' ');
  return (
    <Table<EntityTableRow>
      key={`${userFilter}-${filters.kind?.value}`}
      isLoading={loading}
      columns={columns || defaultColumns}
      options={{
        paging: showPagination,
        pageSize: 20,
        actionsColumnIndex: -1,
        loadingType: 'linear',
        showEmptyDataSourceMessage: !loading,
        padding: 'dense',
        pageSizeOptions: [20, 50, 100],
        ...tableOptions,
      }}
      title={`${titleDisplay} (${entities.length})`}
      data={rows}
      actions={actions || defaultActions}
      emptyContent={emptyContent}
    />
  );
};

EntityTable.columns = columnFactories;
